import { defineStore } from 'pinia'

function normalizeBotStatus(status) {
  const value = String(status || '').trim().toLowerCase()
  if (['running', 'active', 'resume', 'resumed', 'start', 'started'].includes(value)) return 'running'
  if (['paused', 'pause'].includes(value)) return 'paused'
  if (value === 'error') return 'error'
  if (['stopped', 'stop', 'stopping', 'inactive', 'disabled'].includes(value)) return 'stopped'
  return value || 'stopped'
}

function botAccountSortKey(bot) {
  const exchange = String(bot?.exchange || '').trim().toLowerCase()
  const accountKey = String(bot?.accountKey || '').trim().toLowerCase()
  const sourceBotId = String(bot?.sourceBotId || bot?._id || '').trim().toLowerCase()
  return `${exchange}::${accountKey || sourceBotId}`
}

function compareBotsByAccountAndId(a, b) {
  const byAccount = botAccountSortKey(a).localeCompare(botAccountSortKey(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
  if (byAccount !== 0) return byAccount
  return String(a?.botId || '').localeCompare(String(b?.botId || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

function workerSortKey(worker) {
  const hostname = String(worker?.hostname || '').trim()
  const workerId = String(worker?.workerId || '').trim()
  return (hostname || workerId).toLowerCase()
}

function compareWorkersByName(a, b) {
  const left = workerSortKey(a)
  const right = workerSortKey(b)
  const byName = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' })
  if (byName !== 0) return byName
  return String(a?.workerId || '').localeCompare(String(b?.workerId || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

function summarize(workers, bots) {
  const ws = Array.isArray(workers) ? workers : []
  const bs = Array.isArray(bots) ? bots.map((b) => ({ ...b, status: normalizeBotStatus(b?.status) })) : []
  const onlineWs = ws.filter((w) => w.connected || w.status === 'online').length
  const maintenanceWs = ws.filter((w) => w.status === 'maintenance').length
  const offlineWs = Math.max(0, ws.length - onlineWs - maintenanceWs)
  return {
    totalWorkers: ws.length,
    onlineWorkers: onlineWs,
    offlineWorkers: offlineWs,
    maintenanceWorkers: maintenanceWs,
    totalBots: bs.length,
    runningBots: bs.filter((b) => b.status === 'running').length,
    stoppedBots: bs.filter((b) => b.status === 'stopped').length,
    pausedBots: bs.filter((b) => b.status === 'paused').length,
    errorBots: bs.filter((b) => b.status === 'error').length,
  }
}

export const useAdminStateStore = defineStore('adminState', {
  state: () => ({
    workers: [],
    bots: [],
    panicSwitches: [],
    commandEvents: [],
    lastUpdateMs: 0,
    filter: {
      workerId: '',
      botStatus: '',
      botStrategy: '',
      botSearch: '',
    },
  }),
  getters: {
    summary(state) {
      return summarize(state.workers, state.bots)
    },
    filteredWorkers(state) {
      const kw = (state.filter.workerId || '').toLowerCase()
      return state.workers
        .filter((w) => !kw || (w.workerId || '').toLowerCase().includes(kw) || (w.hostname || '').toLowerCase().includes(kw))
        .sort(compareWorkersByName)
    },
    filteredBots(state) {
      const s = (state.filter.botStatus || '').toLowerCase()
      const st = (state.filter.botStrategy || '').toLowerCase()
      const kw = (state.filter.botSearch || '').toLowerCase()
      return state.bots.filter((b) => {
        if (s && normalizeBotStatus(b.status) !== s) return false
        if (st && !(b.strategy || '').toLowerCase().includes(st)) return false
        if (kw) {
          const hay = `${b.botId || ''} ${b.name || ''} ${b.description || ''} ${b.symbol || ''} ${b.exchange || ''} ${b.userId || ''} ${b.workerId || ''}`.toLowerCase()
          if (!hay.includes(kw)) return false
        }
        return true
      }).sort(compareBotsByAccountAndId)
    },
    tier1Enabled(state) {
      return state.panicSwitches.find((s) => s.tier === 1)?.enabled === true
    },
    tier2Enabled(state) {
      return state.panicSwitches.find((s) => s.tier === 2)?.enabled === true
    },
    tier3Enabled(state) {
      return state.panicSwitches.find((s) => s.tier === 3)?.enabled === true
    },
  },
  actions: {
    setWorkers(ws) {
      this.workers = Array.isArray(ws) ? [...ws].sort(compareWorkersByName) : []
      this.lastUpdateMs = Date.now()
    },
    setBots(bs) {
      this.bots = Array.isArray(bs)
        ? bs.map((b) => ({
            ...b,
            status: normalizeBotStatus(b?.status),
          })).sort(compareBotsByAccountAndId)
        : []
      this.lastUpdateMs = Date.now()
    },
    setPanicSwitches(arr) {
      this.panicSwitches = Array.isArray(arr) ? arr : []
      this.lastUpdateMs = Date.now()
    },
    setFilter(patch) {
      this.filter = { ...this.filter, ...(patch || {}) }
    },
    prependCommandEvent(ev) {
      this.commandEvents.unshift({ ...ev, at: Date.now() })
      if (this.commandEvents.length > 50) this.commandEvents.pop()
    },
  },
})
